import { localDateString } from '../../../utils/localDate';

export const MODE_JOB = 'job';
export const MODE_LOCATION = 'location';
// Локация, которой нет в справочнике — рабочий вбивает её руками. Так же
// устроена Initial Incident Communication (Job / Location / Manual).
export const MODE_MANUAL = 'manual';

/**
 * Режим, в котором объект описан локацией (из справочника или введённой руками),
 * а не номером джоба. Значение лежит в stepOne.locationName в обоих случаях.
 */
export function isLocationMode(mode) {
    return mode === MODE_LOCATION || mode === MODE_MANUAL;
}

// Document types that have special handling (matched by value from external service)
export const SPECIAL_DOC_TYPES = {
    'Confined Space': { isSpecial: true, specialType: 'csep' },
    'Lockout/Tagout': { isSpecial: true, specialType: 'ltt' }
};

/**
 * Create blank Step 1: General Information + Evaluation
 */
export function createBlankStepOne(evaluation = [], defaultPerson = '', today) {
    const todayString = today || localDateString();
    return {
        mode: MODE_JOB,
        jobNumber: '',
        jobSiteName: '',
        projectName: '',
        jobSiteAddress: '',
        locationName: '',
        date: todayString,
        taskLocation: '',
        personCompleting: defaultPerson || '',
        jobSiteSupervisor: defaultPerson || '',
        evaluationOfWork: evaluation.map(ev => ({ value: ev.value, selected: '' }))
    };
}

/**
 * Create blank Step 2: Documents & Permits
 */
export function createBlankStepTwo(docs = []) {
    return {
        docsPermits: docs.map(doc => {
            // Check if this doc type has special handling
            const specialConfig = SPECIAL_DOC_TYPES[doc.value] || {};
            return {
                value: doc.value,
                required: 'Not Req',
                attachments: [],
                isSpecial: specialConfig.isSpecial || false,
                specialType: specialConfig.specialType || null,
                linkedCsepUuid: null,
                linkedLttUuid: null
            };
        }),
        customDocsPermits: []
    };
}

/**
 * Create blank Step 3: Work Activities + Energy
 */
export function createBlankStepThree() {
    return {
        weatherConditions: [],
        tasks: [],
        customTasks: [],
        workActivities: [],
        customWorkActivities: [],
        energyExposures: [],
        energyActivityRows: [],
        sequenceOfJobSteps: '',
        preShiftSignature: '',
        initialSubmission: null
    };
}

/**
 * Ensure and migrate all steps structure
 */
export function ensureAllStepsStructure(record, docs, evaluation, defaultPerson = '', today) {
    const result = { ...record };

    // Check if old structure exists (stepTwo contains everything)
    const hasOldStructure = result.stepTwo && (
        result.stepTwo.mode !== undefined ||
        result.stepTwo.evaluationOfWork !== undefined ||
        result.stepTwo.energyActivityRows !== undefined
    );

    if (hasOldStructure) {
        // MIGRATE FROM OLD STRUCTURE
        const oldStepTwo = result.stepTwo || {};

        // Migrate to Step 1: General Info + Evaluation
        result.stepOne = createBlankStepOne(evaluation, defaultPerson, today);
        result.stepOne.mode = oldStepTwo.mode || MODE_JOB;
        result.stepOne.jobNumber = oldStepTwo.jobNumber || '';
        result.stepOne.jobSiteName = oldStepTwo.jobSiteName || '';
        result.stepOne.projectName = oldStepTwo.projectName || '';
        result.stepOne.jobSiteAddress = oldStepTwo.jobSiteAddress || '';
        result.stepOne.locationName = oldStepTwo.locationName || '';
        result.stepOne.date = oldStepTwo.date || (today || localDateString());
        result.stepOne.taskLocation = oldStepTwo.taskLocation || '';
        result.stepOne.personCompleting = oldStepTwo.personCompleting || defaultPerson || '';
        result.stepOne.jobSiteSupervisor = oldStepTwo.jobSiteSupervisor || defaultPerson || '';

        // Migrate evaluation
        const evaluationMap = new Map((oldStepTwo.evaluationOfWork || []).map(ev => [ev.value, ev]));
        if (evaluation && Array.isArray(evaluation) && evaluation.length) {
            // If evaluation list provided, use it and merge with existing selections
            result.stepOne.evaluationOfWork = evaluation.map(ev => {
                const existing = evaluationMap.get(ev.value);
                const selected = existing?.selected;
                return {
                    value: ev.value,
                    selected: selected === 'YES' || selected === 'N/A' ? selected : ''
                };
            });
        } else {
            // If no evaluation list provided (null/undefined), preserve existing
            result.stepOne.evaluationOfWork = (oldStepTwo.evaluationOfWork || []).map(item => ({
                value: item.value,
                selected: item.selected === 'YES' || item.selected === 'N/A' ? item.selected : ''
            }));
        }

        // Migrate to Step 2: Documents
        if (docs && Array.isArray(docs) && docs.length) {
            // If docs list provided, create new structure
            result.stepTwo = createBlankStepTwo(docs);
            // Merge existing selections
            const existingDocsMap = new Map((oldStepTwo.docsPermits || []).map(d => [d.value, d]));
            result.stepTwo.docsPermits = result.stepTwo.docsPermits.map(doc => {
                const existing = existingDocsMap.get(doc.value);
                return existing || doc;
            });
        } else {
            // If no docs list provided (null/undefined), preserve existing
            result.stepTwo = {
                docsPermits: oldStepTwo.docsPermits || [],
                customDocsPermits: oldStepTwo.customDocsPermits || []
            };
        }

        // Migrate to Step 3: Work Activities + Energy
        result.stepThree = createBlankStepThree();
        result.stepThree.weatherConditions = oldStepTwo.weatherConditions || [];
        result.stepThree.tasks = oldStepTwo.tasks || [];
        result.stepThree.customTasks = oldStepTwo.customTasks || [];
        result.stepThree.workActivities = oldStepTwo.workActivities || [];
        result.stepThree.customWorkActivities = oldStepTwo.customWorkActivities || [];
        result.stepThree.sequenceOfJobSteps = oldStepTwo.sequenceOfJobSteps || '';
        result.stepThree.preShiftSignature = oldStepTwo.preShiftSignature || oldStepTwo.supervisorSignature || '';
        result.stepThree.initialSubmission = oldStepTwo.initialSubmission || null;

        // Migrate energy data
        if (!Array.isArray(result.stepThree.energyActivityRows)) {
            result.stepThree.energyActivityRows = [];
        }

        // If we have old energyExposures but no energyActivityRows, migrate them
        if (Array.isArray(oldStepTwo.energyExposures) && oldStepTwo.energyExposures.length > 0 && result.stepThree.energyActivityRows.length === 0) {
            result.stepThree.energyActivityRows = oldStepTwo.energyExposures.map((exp, index) => ({
                id: exp.id || `migrated-row-${Date.now()}-${index}`,
                workActivity: exp.workActivity || '',
                actions: exp.actions || '',
                energySelections: exp.energyId ? [{
                    id: `migrated-sel-${Date.now()}-${index}`,
                    energyKey: exp.energyKey || null,
                    energyId: exp.energyId || null,
                    energyName: exp.energyName || exp.value || '',
                    hazards: Array.isArray(exp.hazards) ? exp.hazards : []
                }] : []
            }));
        } else if (Array.isArray(oldStepTwo.energyActivityRows) && oldStepTwo.energyActivityRows.length > 0) {
            result.stepThree.energyActivityRows = oldStepTwo.energyActivityRows;
        }

        if (!Array.isArray(result.stepThree.energyExposures)) {
            result.stepThree.energyExposures = [];
        }
        if (result.stepThree.energyActivityRows.length > 0) {
            result.stepThree.energyExposures = result.stepThree.energyActivityRows;
        }

        // Migrate Step 4: Crew Selection
        if (!result.stepFour || !result.stepFour.employeesSelected) {
            result.stepFour = {
                employeesSelected: result.stepOne?.employeesSelected || []
            };
        }

        // Migrate Step 5: Final Review
        if (result.stepFour && (result.stepFour.checklist || result.stepFour.comments)) {
            // Old stepFour had final review data
            result.stepFive = {
                checklist: result.stepFour.checklist || {},
                checklistNotes: result.stepFour.checklistNotes || {},
                comments: result.stepFour.comments || '',
                supervisorSignature: result.stepFour.supervisorSignature || '',
                completedAt: result.stepFour.completedAt || null
            };
            // Clear old stepFour
            result.stepFour = {
                employeesSelected: result.stepOne?.employeesSelected || []
            };
        }
    } else {
        // NEW STRUCTURE - just ensure all steps exist
        if (!result.stepOne) {
            result.stepOne = createBlankStepOne(
                evaluation && Array.isArray(evaluation) ? evaluation : [],
                defaultPerson,
                today
            );
        } else if (evaluation && Array.isArray(evaluation) && evaluation.length) {
            // stepOne exists - sync evaluation list with existing selections
            const existingEvalMap = new Map(
                (result.stepOne.evaluationOfWork || []).map(ev => [ev.value, ev.selected])
            );
            result.stepOne.evaluationOfWork = evaluation.map(ev => ({
                value: ev.value,
                selected: existingEvalMap.get(ev.value) || ''
            }));
        }

        if (!result.stepTwo) {
            result.stepTwo = createBlankStepTwo(
                docs && Array.isArray(docs) ? docs : []
            );
        } else if (docs && Array.isArray(docs) && docs.length) {
            // stepTwo exists - sync docs list with existing selections
            const existingDocsMap = new Map(
                (result.stepTwo.docsPermits || []).map(d => [d.value, d])
            );

            result.stepTwo.docsPermits = docs.map(doc => {
                const existing = existingDocsMap.get(doc.value);
                const specialConfig = SPECIAL_DOC_TYPES[doc.value] || {};
                if (existing) {
                    // Preserve existing but ensure special flags are set
                    return {
                        ...existing,
                        isSpecial: specialConfig.isSpecial || existing.isSpecial || false,
                        specialType: specialConfig.specialType || existing.specialType || null
                    };
                }
                return {
                    value: doc.value,
                    required: 'Not Req',
                    attachments: [],
                    isSpecial: specialConfig.isSpecial || false,
                    specialType: specialConfig.specialType || null,
                    linkedCsepUuid: null,
                    linkedLttUuid: null
                };
            });
        }

        if (!result.stepThree) {
            result.stepThree = createBlankStepThree();
        }

        if (!result.stepFour) {
            result.stepFour = {
                employeesSelected: []
            };
        }

        if (!result.stepFive) {
            result.stepFive = {
                checklist: {},
                checklistNotes: {},
                comments: '',
                supervisorSignature: '',
                completedAt: null
            };
        }
    }

    // Ensure arrays are valid
    if (!Array.isArray(result.stepOne.evaluationOfWork)) {
        result.stepOne.evaluationOfWork = [];
    }
    if (!Array.isArray(result.stepTwo.docsPermits)) {
        result.stepTwo.docsPermits = [];
    }
    if (!Array.isArray(result.stepTwo.customDocsPermits)) {
        result.stepTwo.customDocsPermits = [];
    }
    if (!Array.isArray(result.stepThree.energyActivityRows)) {
        result.stepThree.energyActivityRows = [];
    }
    if (!Array.isArray(result.stepFour.employeesSelected)) {
        result.stepFour.employeesSelected = [];
    }

    return result;
}

// Legacy exports for backward compatibility (deprecated)
export const createBlankStepTwo_DEPRECATED = createBlankStepTwo;
export const ensureStepTwoStructure = ensureAllStepsStructure;